import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';

export interface UseWarrantyExpiredAssetsReturn {
  uniqueAssets: any[];
  hasExpiredAssets: boolean;
  isAssetExpiredForResident: (warrantyExpiration: string) => boolean;
  notifyWarrantyExpired: (assetName: string) => void;
}

const RESIDENT_ROLE_NAMES: string[] = ['Home Owner', 'Occupant'];

const MILLISECONDS_PER_DAY: number = 1000 * 60 * 60 * 24;

// legacy/workaround: BOS counted the final 24h before the expiry timestamp as already expired
const FINAL_DAY_COUNTS_AS_EXPIRED_OFFSET: number = 1;

const isPastWarrantyEnd = (warrantyExpiration: string): boolean => {
  const expiryDate: Date = new Date(warrantyExpiration);
  const today: Date = new Date();

  const millisecondsUntilExpiry: number =
    expiryDate.getTime() - today.getTime();
  const daysUntilExpiry: number = millisecondsUntilExpiry / MILLISECONDS_PER_DAY;
  const daysRemaining: number =
    daysUntilExpiry - FINAL_DAY_COUNTS_AS_EXPIRED_OFFSET;

  return daysRemaining < 0;
};

const dedupeAssetsByItemName = (assets: any[]): any[] => {
  const assetsWithItems: any[] = assets.filter(
    (asset: any) => asset.item !== null
  );

  if (assetsWithItems.length === 0) {
    return assets;
  }

  const latestAssetPerItemName: Map<string, any> = new Map(
    assetsWithItems.map((asset: any) => [asset.item.name, asset])
  );

  const uniqueAssets: any[] = [...latestAssetPerItemName.values()];

  return uniqueAssets;
};

export const useWarrantyExpiredAssets = (
  assets: any[] = []
): UseWarrantyExpiredAssetsReturn => {
  const user: any = useSelector(selectAuthenticatedUser);

  const isResident: boolean = RESIDENT_ROLE_NAMES.includes(
    user?.roles?.[0]?.role?.name
  );

  const isAssetExpiredForResident = (warrantyExpiration: string): boolean => {
    if (!isResident) {
      return false;
    }

    if (!warrantyExpiration) {
      return false;
    }

    return isPastWarrantyEnd(warrantyExpiration);
  };

  const uniqueAssets: any[] = useMemo(
    () => dedupeAssetsByItemName(assets),
    [assets]
  );

  const hasExpiredAssets: boolean = uniqueAssets.some((asset: any) =>
    isAssetExpiredForResident(asset.warrantExpiration)
  );

  const notifyWarrantyExpired = (assetName: string) => {
    const displayName: string = assetName || 'Selected Asset';

    toast.error(
      ` ⓘ We're sorry, but ${displayName} is currently past your warranty end date. Please contact us should you have any questions or concerns regarding your warranty.`,
      { autoClose: 6000, position: 'top-center' }
    );
  };

  return {
    uniqueAssets,
    hasExpiredAssets,
    isAssetExpiredForResident,
    notifyWarrantyExpired,
  };
};